'use client';

import type { FC } from 'react';

import { useAuiState } from '@assistant-ui/react';
import { AlertCircle } from 'lucide-react';
import { useTranslations } from 'next-intl';

import {
  resolveThreadStatusKey,
  useThreadStatusStore,
  type ThreadStatus,
} from '@/lib/thread-status/store';
import { cn } from '@/lib/utils';

// One distinct colour per state, in hue and motion (the Slack/Linear/Teams convention): activity
// pulses in the accent, an unseen reply is a steady green dot, needs-attention is an amber ring
// (shape as well as hue), and error is a red icon (below).
const DOT_STYLES: Record<Exclude<ThreadStatus, 'error'>, string> = {
  streaming: 'bg-primary motion-safe:animate-pulse',
  'awaiting-approval': 'border-2 border-amber-600 dark:border-amber-400',
  unread: 'bg-emerald-600 dark:bg-emerald-400',
};

export const STATUS_LABEL_KEYS: Record<ThreadStatus, string> = {
  streaming: 'threadList.statusStreaming',
  'awaiting-approval': 'threadList.statusAwaitingApproval',
  error: 'threadList.statusError',
  unread: 'threadList.statusUnread',
};

// Shared row-slot behavior: logical-end placement (RTL-safe) and hidden on the active row and
// wherever the row menu shows (hover/focus on fine pointers) — the menu occupies the same slot.
const SLOT_CLASSES =
  'me-2.5 shrink-0 group-data-active/thread-item:hidden pointer-fine:group-focus-within/thread-item:hidden pointer-fine:group-hover/thread-item:hidden';

export function useThreadListItemStatus(): ThreadStatus | undefined {
  const statusKey = useAuiState(({ threadListItem }) =>
    threadListItem ? resolveThreadStatusKey(threadListItem) : null,
  );
  return useThreadStatusStore(state => (statusKey ? state.statuses[statusKey] : undefined));
}

/**
 * Compact status indicator at the end of a thread list row: a colored dot per state, except
 * error which renders a small alert icon — color alone is a weak signal for the one state
 * that demands action (WCAG 1.4.1 use-of-color).
 */
export const ThreadListItemStatusIndicator: FC = () => {
  const t = useTranslations();
  const status = useThreadListItemStatus();

  if (!status) return null;

  if (status === 'error') {
    return (
      <span
        role="status"
        aria-label={t(STATUS_LABEL_KEYS.error)}
        className={cn(SLOT_CLASSES, 'text-destructive')}
      >
        <AlertCircle aria-hidden className="size-3" />
      </span>
    );
  }

  return (
    <span
      role="status"
      aria-label={t(STATUS_LABEL_KEYS[status])}
      className={cn(SLOT_CLASSES, 'size-2 rounded-full', DOT_STYLES[status])}
    />
  );
};
